import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiTrash2, FiCheck } from 'react-icons/fi';

export default function SignatureModal({ title, subtitle, name, onSign, onCancel }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const lastPos = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e293b';
    ctxRef.current = ctx;
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
    setIsDrawing(true);
    setIsEmpty(false);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
    lastPos.current = null;
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const handleSign = () => {
    if (isEmpty) return;
    const canvas = canvasRef.current;
    onSign(canvas.toDataURL('image/png'));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 20, padding: '28px',
          width: '90%', maxWidth: 520, boxShadow: '0 24px 64px rgba(0,0,0,0.35)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              {title}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{subtitle}</p>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        {/* Signer info */}
        {name && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, background: 'var(--bg-input)',
            border: '1px solid var(--border)', marginBottom: 16,
            fontSize: 13, fontWeight: 600, color: 'var(--text-primary)'
          }}>
            Signing as: <span style={{ color: '#2563eb' }}>{name}</span>
          </div>
        )}

        {/* Canvas area */}
        <div style={{
          border: '2px dashed var(--border)', borderRadius: 12,
          background: '#ffffff', position: 'relative', marginBottom: 8,
          cursor: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cline x1='12' y1='2' x2='12' y2='22' stroke='%23000' stroke-width='1.5'/%3E%3Cline x1='2' y1='12' x2='22' y2='12' stroke='%23000' stroke-width='1.5'/%3E%3C/svg%3E\") 12 12, crosshair",
          overflow: 'hidden'
        }}>
          {isEmpty && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', pointerEvents: 'none',
              fontSize: 14, color: '#94a3b8', fontStyle: 'italic', userSelect: 'none'
            }}>
              Sign here
            </div>
          )}
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            style={{ display: 'block', width: '100%', height: 160, touchAction: 'none', cursor: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cline x1='12' y1='2' x2='12' y2='22' stroke='%23000' stroke-width='1.5'/%3E%3Cline x1='2' y1='12' x2='22' y2='12' stroke='%23000' stroke-width='1.5'/%3E%3C/svg%3E\") 12 12, crosshair" }}
          />
        </div>

        {/* Signature line label */}
        <div style={{ borderTop: '1px solid #94a3b8', marginBottom: 16, paddingTop: 4, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
          Signature
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={clear}
            disabled={isEmpty}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)',
              color: isEmpty ? 'var(--text-muted)' : '#dc2626', fontSize: 13, fontWeight: 600,
              cursor: isEmpty ? 'not-allowed' : 'pointer', opacity: isEmpty ? 0.5 : 1
            }}
          >
            <FiTrash2 size={14} /> Clear
          </button>
          <button onClick={onCancel} style={{
            flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--border)',
            background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 14,
            fontWeight: 600, cursor: 'pointer'
          }}>
            Cancel
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={handleSign}
            disabled={isEmpty}
            style={{
              flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 16px', borderRadius: 10, border: 'none',
              background: isEmpty ? 'var(--border)' : '#10b981',
              color: isEmpty ? 'var(--text-muted)' : '#fff',
              fontSize: 14, fontWeight: 700, cursor: isEmpty ? 'not-allowed' : 'pointer'
            }}
          >
            <FiCheck size={16} /> Sign & Confirm
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
